import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  confirmEmail,
  confirmWhatsapp,
  fetchCurrentUser,
  forgotPassword,
  resetPassword,
  signIn,
  signUp,
  socialSignIn,
  socialSignUp,
  spoofedInUser,
  spoofedOutUser,
  updateUser,
  updateUserImage,
  updateUserPlan,
} from './user.action';

import { AppError } from '../../entities/Errors';
import { User } from '../../entities/User';

import { isImpersonationToken, isTokenValid } from '../../utils/token';

import authToken from '../../services/auth_token.service';
import { signOutService } from '../../services/user.service';

const token = authToken();

const isLoggedIn = token ? isTokenValid(token) : false;

// Token vencido no boot: limpa a sessão salva em vez de mantê-la "meio viva".
// Antes o user e o token ficavam no localStorage e a faixa de impersonação
// aparecia na tela de login com o nome do cliente ("Você está acessando como
// <cliente>") — o backend já recusava o token, mas o nome do cliente ficava
// exposto em computador compartilhado (relato do Bruno, out/2026).
if (token && !isLoggedIn) signOutService();

const localStorageUser = localStorage.getItem('user');

const user = localStorageUser ? (JSON.parse(localStorageUser) as User) : null;

// Sessão impersonada sobrevive a F5: a marca (adminId) está no próprio JWT —
// mas só enquanto o token é válido.
const isImpersonating =
  token && isLoggedIn ? isImpersonationToken(token) : false;

interface UserState {
  action:
    | 'idle'
    | 'signIn'
    | 'spoofedInUser'
    | 'spoofedOutUser'
    | 'signUp'
    | 'confirmEmail'
    | 'confirmWhatsapp'
    | 'resetPassword'
    | 'forgotPassword'
    | 'updateUser'
    | 'updateImage'
    | 'updatePlan'
    | 'refresh';
  status: 'idle' | 'loading' | 'failed' | 'successed';
  role: 'client' | 'admin';
  error: null | AppError;
  user: User | null;
  isLoggedIn: boolean;
  // true enquanto um admin está "acessando como" um cliente
  isImpersonating: boolean;
  refresh: boolean;
}

const initialState = {
  action: 'idle',
  status: 'idle',
  role: 'client',
  error: null,
  user: user,
  isLoggedIn: isLoggedIn,
  isImpersonating: isImpersonating,
  refresh: false,
} as UserState;

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    signOut: (state) => {
      signOutService();
      state.isLoggedIn = false;
      state.isImpersonating = false;
      state.user = null;
    },
    refresh: (state) => {
      state.error = null;
      state.action = 'idle';
      state.status = 'idle';
    },
    setRole: (state, action: PayloadAction<'client' | 'admin'>) => {
      state.role = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(signUp.pending, (state) => {
        state.action = 'signUp';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.status = 'successed';
        state.user = action.payload.data.user;
        state.isLoggedIn = action.payload.data.token ? true : false;
        state.isImpersonating = false;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(socialSignUp.pending, (state) => {
        state.action = 'signUp';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(socialSignUp.fulfilled, (state, action) => {
        state.status = 'successed';
        state.user = action.payload.data.user;
        state.isLoggedIn = action.payload.data.token ? true : false;
        state.isImpersonating = false;
      })
      .addCase(socialSignUp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(signIn.pending, (state) => {
        state.action = 'signIn';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.status = 'successed';
        state.user = action.payload.data.user;
        state.isLoggedIn = action.payload.data.token ? true : false;
        // login novo nunca é impersonado — desliga a faixa de sessão anterior
        state.isImpersonating = false;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(spoofedInUser.pending, (state) => {
        state.action = 'spoofedInUser';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(spoofedInUser.fulfilled, (state, action) => {
        state.status = 'successed';
        state.user = action.payload.data.user;
        state.isLoggedIn = action.payload.data.token ? true : false;
        state.isImpersonating = true;
      })
      .addCase(spoofedInUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(spoofedOutUser.pending, (state) => {
        state.action = 'spoofedOutUser';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(spoofedOutUser.fulfilled, (state, action) => {
        state.status = 'successed';
        state.user = action.payload.data.user;
        state.isLoggedIn = action.payload.data.token ? true : false;
        state.isImpersonating = false;
      })
      .addCase(spoofedOutUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(socialSignIn.pending, (state) => {
        state.action = 'signIn';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(socialSignIn.fulfilled, (state, action) => {
        state.status = 'successed';
        state.user = action.payload.data.user;
        state.isLoggedIn = action.payload.data.token ? true : false;
        state.isImpersonating = false;
      })
      .addCase(socialSignIn.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(confirmEmail.pending, (state) => {
        state.action = 'confirmEmail';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(confirmEmail.fulfilled, (state) => {
        state.status = 'successed';
      })
      .addCase(confirmEmail.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(confirmWhatsapp.pending, (state) => {
        state.action = 'confirmWhatsapp';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(confirmWhatsapp.fulfilled, (state) => {
        state.status = 'successed';
      })
      .addCase(confirmWhatsapp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(forgotPassword.pending, (state) => {
        state.action = 'forgotPassword';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.status = 'successed';
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(resetPassword.pending, (state) => {
        state.action = 'resetPassword';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.status = 'successed';
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      .addCase(updateUser.pending, (state) => {
        state.action = 'updateUser';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.status = 'successed';
        state.user = action.payload.data;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });
    builder
      .addCase(updateUserImage.pending, (state) => {
        state.status = 'loading';
        state.action = 'updateImage';
        state.error = null;
      })
      .addCase(updateUserImage.fulfilled, (state, action) => {
        state.status = 'successed';
        state.user = action.payload.data;
      })
      .addCase(updateUserImage.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });

    builder
      // Re-hidratação silenciosa: atualiza o usuário (e o plano) sem mexer em
      // action/status para não disparar efeitos de outras telas.
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.user = action.payload.data;
        }
      });

    builder
      .addCase(updateUserPlan.pending, (state) => {
        state.action = 'updatePlan';
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateUserPlan.fulfilled, (state, action) => {
        state.status = 'successed';
        state.user = action.payload.data;
      })
      .addCase(updateUserPlan.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as AppError;
      });
  },
});

export const { refresh: userRefresh, signOut, setRole } = userSlice.actions;

export default userSlice.reducer;
